import { useState, useEffect } from 'react'
import { productGridService } from '../../../services/erpApi'
import { Grid, Plus, Search, Loader2 } from 'lucide-react'
import Modal from '../Modal/Modal'
import './ProductGridManager.css'

/**
 * Componente para gerenciar grade de produtos
 * Permite criar, selecionar e vincular produtos a grades
 */
function ProductGridManager({ productId, currentGridId, onGridChange }) {
  const [grids, setGrids] = useState([])
  const [selectedGridId, setSelectedGridId] = useState(currentGridId || null)
  const [selectedGrid, setSelectedGrid] = useState(null)
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showGridModal, setShowGridModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  
  const [newGrid, setNewGrid] = useState({
    name: '',
    baseName: '',
    description: ''
  })

  useEffect(() => {
    if (currentGridId) {
      setSelectedGridId(currentGridId)
      loadGrid(currentGridId)
    }
    loadGrids()
  }, [currentGridId])

  const loadGrids = async () => {
    try {
      setLoading(true)
      const response = await productGridService.getAll()
      if (response.success) {
        setGrids(response.data)
      }
    } catch (error) {
      console.error('Erro ao carregar grades:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadGrid = async (gridId) => {
    try {
      const response = await productGridService.getById(gridId)
      if (response.success) {
        setSelectedGrid(response.data)
      }
    } catch (error) {
      console.error('Erro ao carregar grade:', error)
    }
  }

  const handleSelectGrid = async (gridId) => {
    if (!gridId) {
      // Remover produto da grade
      if (selectedGridId && productId) {
        try {
          await productGridService.removeProduct(selectedGridId, productId)
          setSelectedGridId(null)
          setSelectedGrid(null)
          onGridChange?.(null)
        } catch (error) {
          console.error('Erro ao remover produto da grade:', error)
          alert('Erro ao remover produto da grade')
        }
      }
      return
    }

    try {
      setLoading(true)
      if (selectedGridId && productId) {
        // Remover da grade anterior
        await productGridService.removeProduct(selectedGridId, productId)
      }
      
      // Adicionar à nova grade
      await productGridService.addProduct(gridId, productId)
      setSelectedGridId(gridId)
      await loadGrid(gridId)
      onGridChange?.(gridId)
      setShowGridModal(false)
    } catch (error) {
      console.error('Erro ao associar produto à grade:', error)
      alert('Erro ao associar produto à grade')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateGrid = async () => {
    if (!newGrid.name || !newGrid.baseName) {
      alert('Nome e nome base são obrigatórios')
      return
    }

    try {
      setCreating(true)
      const response = await productGridService.create(newGrid)
      if (response.success) {
        await loadGrids()
        setNewGrid({ name: '', baseName: '', description: '' })
        setShowCreateModal(false)
        
        // Automaticamente associar produto à nova grade
        if (productId) {
          await handleSelectGrid(response.data.id)
        }
      }
    } catch (error) {
      console.error('Erro ao criar grade:', error)
      alert('Erro ao criar grade')
    } finally {
      setCreating(false)
    }
  }

  // Filtrar grades por termo de busca (nome ou base_name)
  const filteredGrids = grids.filter(grid => {
    if (!searchTerm || !searchTerm.trim()) {
      return true // Se não houver termo de busca, mostrar todos
    }
    const searchLower = searchTerm.toLowerCase().trim()
    const gridName = (grid.name || '').toLowerCase()
    const gridBaseName = (grid.base_name || '').toLowerCase()
    return gridName.includes(searchLower) || gridBaseName.includes(searchLower)
  })

  return (
    <div className="product-grid-manager">
      <div className="grid-header">
        <Grid size={20} />
        <h3>Grade do Produto</h3>
      </div>

      {selectedGrid ? (
        <div className="current-grid">
          <div className="grid-info">
            <div className="grid-name">{selectedGrid.name}</div>
            <div className="grid-base-name">{selectedGrid.base_name}</div>
            {selectedGrid.product_count > 0 && (
              <div className="grid-products-count">
                {selectedGrid.product_count} produto(s) nesta grade
              </div>
            )}
          </div>
          <div className="grid-actions">
            <button
              className="btn-change-grid"
              onClick={() => {
                setSearchTerm('') // Resetar busca ao abrir modal
                setShowGridModal(true)
              }}
            >
              Alterar Grade
            </button>
            <button
              className="btn-remove-grid"
              onClick={() => handleSelectGrid(null)}
              disabled={loading}
            >
              Remover da Grade
            </button>
          </div>
        </div>
      ) : (
        <div className="no-grid">
          <p>Este produto não está associado a nenhuma grade.</p>
          <div className="no-grid-actions">
            <button
              className="btn-select-grid"
              onClick={() => {
                setSearchTerm('') // Resetar busca ao abrir modal
                setShowGridModal(true)
              }}
            >
              <Search size={16} />
              Selecionar Grade Existente
            </button>
            <button
              className="btn-create-grid"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus size={16} />
              Criar Nova Grade
            </button>
          </div>
        </div>
      )}

      {/* Modal para criar nova grade */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Criar Nova Grade"
      >
        <div className="create-grid-form">
          <div className="form-group">
            <label>Nome da Grade *</label>
            <input
              type="text"
              value={newGrid.name}
              onChange={(e) => setNewGrid({ ...newGrid, name: e.target.value })}
              placeholder="Ex: YOGA-MAT-2402"
              required
            />
            <small>Identificador único da grade (maiúsculas recomendado)</small>
          </div>
          
          <div className="form-group">
            <label>Nome Base *</label>
            <input
              type="text"
              value={newGrid.baseName}
              onChange={(e) => setNewGrid({ ...newGrid, baseName: e.target.value })}
              placeholder="Ex: Yoga Mat Com Alca Em EVA Alux"
              required
            />
            <small>Nome descritivo do produto</small>
          </div>
          
          <div className="form-group">
            <label>Descrição</label>
            <textarea
              value={newGrid.description}
              onChange={(e) => setNewGrid({ ...newGrid, description: e.target.value })}
              rows="3"
              placeholder="Descrição opcional da grade"
            />
          </div>
          
          <div className="form-actions">
            <button
              className="btn-cancel"
              onClick={() => setShowCreateModal(false)}
            >
              Cancelar
            </button>
            <button
              className="btn-save"
              onClick={handleCreateGrid}
              disabled={creating}
            >
              {creating ? <Loader2 className="spinner" size={16} /> : <Plus size={16} />}
              Criar Grade
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal para selecionar grade */}
      <Modal
        isOpen={showGridModal}
        onClose={() => {
          setShowGridModal(false)
          setSearchTerm('') // Resetar busca ao fechar
        }}
        title="Selecionar Grade"
      >
        <div className="select-grid-modal">
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Buscar grade por nome ou nome base..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>

          <div className="grids-list">
            {loading ? (
              <div className="loading">
                <Loader2 className="spinner" size={24} />
                <span>Carregando grades...</span>
              </div>
            ) : filteredGrids.length === 0 ? (
              <div className="no-results">
                <p>Nenhuma grade encontrada</p>
              </div>
            ) : (
              filteredGrids.map(grid => (
                <div
                  key={grid.id}
                  className={`grid-item ${selectedGridId === grid.id ? 'selected' : ''}`}
                  onClick={() => handleSelectGrid(grid.id)}
                >
                  <div className="grid-item-info">
                    <div className="grid-item-name">{grid.name}</div>
                    <div className="grid-item-base">{grid.base_name}</div>
                    {grid.product_count > 0 && (
                      <div className="grid-item-count">{grid.product_count} produto(s)</div>
                    )}
                  </div>
                  {selectedGridId === grid.id && (
                    <div className="grid-item-check">✓</div>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="modal-actions">
            <button
              className="btn-create-in-modal"
              onClick={() => {
                setShowGridModal(false)
                setShowCreateModal(true)
              }}
            >
              <Plus size={16} />
              Criar Nova Grade
            </button>
            <button
              className="btn-close-modal"
              onClick={() => setShowGridModal(false)}
            >
              Fechar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ProductGridManager

